import { AfterViewInit, Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { BaseChartService } from '../../../../shared/services/base-chart.service';
import { BarChartData } from '../interfaces/bar-chart-data.interface';
import { SpinnerService } from '../../../../shared/services/spinner.service';
import { CommonService } from '../../../../shared/services/common.service';

/**
 * Smoothed Line Graph Component
 * @author: PRachethan | 02-08-2025 
 */
@Component({
  selector: 'bi-smoothed-line-graph',
  standalone: false,
  templateUrl: './smoothed-line-graph.component.html',
  styleUrl: './smoothed-line-graph.component.scss'
})
export class SmoothedLineGraphComponent implements OnInit, AfterViewInit, OnDestroy {
  private root!: am5.Root;
  data: BarChartData[] = [];
  private smoothedLineGraphReportSubscription: any;
  private isSmoothedLineViewInitialized = false;

  @Input() reportData: any = null;

  constructor(
    private zone: NgZone,
    private _baseChartService: BaseChartService,
    private _spinnerService: SpinnerService,
    private _commonService: CommonService
  ) { 
    console.log('SmoothedLineGraphComponent constructor called');
  }

  ngOnInit(): void {
    console.log('SmoothedLineGraphComponent ngOnInit called');
    this.zone.runOutsideAngular(() => {
      this.smoothedLineGraphReportSubscription = this._commonService.graphReportState.subscribe((data: any) => {
        console.log('SmoothedLineGraphComponent received data:', data);
        if (data && data.reportData && Array.isArray(data.reportData) && data.reportData.length > 0) {
          this.reportData = data;
          console.log("Smoothed Line Graph Data", this.reportData);
          this.data = this._baseChartService.transformReportData(data);
          if (this.isSmoothedLineViewInitialized) {
            this.updateChart(this.data);
          }
        } else {
          console.log('SmoothedLineGraphComponent - No valid data received');
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.isSmoothedLineViewInitialized = true;
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
      this.zone.runOutsideAngular(() => {
        this.data = this._baseChartService.transformReportData(this.reportData);
        this.updateChart(this.data);
      });
    }
  }

  ngOnChanges(): void {
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0 && this.isSmoothedLineViewInitialized) {
      this.zone.runOutsideAngular(() => {
        this.data = this._baseChartService.transformReportData(this.reportData);
        this.updateChart(this.data);
      });
    }
  }

  createChart(data: BarChartData[]) {
    // Check if the chart container exists
    const chartContainer = document.getElementById('smoothed-line-chartdiv');
    if (!chartContainer) {
      console.error('Smoothed Line Chart - Chart container not found! Make sure element with id "smoothed-line-chartdiv" exists');
      return;
    }

    // Dispose existing chart before creating new one
    if (this.root) {
      console.log('Smoothed Line Chart - Disposing existing chart...');
      this._baseChartService.disposeChart(this.root);
      this.root = undefined as any;
    }

    try {
      console.log('Smoothed Line Chart - Creating new chart...');
      this.root = this._baseChartService.createRoot('smoothed-line-chartdiv');
      this._baseChartService.attachExporting(this.root, { 
        filePrefix: 'smoothed-line',
        data: data
      });

      const categoryField = this._baseChartService.getCategoryField(data);
      const valueFields = this._baseChartService.getValueFields(data);

      const chart = this._baseChartService.createChartContainer(this.root, {
        panX: true,
        panY: true,
        wheelX: 'panX',
        wheelY: 'zoomX',
        layout: this.root.verticalLayout
      });

      this._baseChartService.setupColorPalette(chart);

      const categoryAxis = this._baseChartService.createCategoryAxis(chart, categoryField, {
        axisType: 'x',
        minGridDistance: 20,
        cellStartLocation: 0.1,
        cellEndLocation: 0.9,
        rotation: 45
      });

      const valueAxis = this._baseChartService.createValueAxis(chart, {
        axisType: 'y'
      });

      // Add cursor (vertical line) – required for series tooltips to show on hover
      const cursor = chart.set('cursor', am5xy.XYCursor.new(this.root, {
        behavior: 'none',
        xAxis: categoryAxis,
        yAxis: valueAxis
      }));
      cursor.lineY.set('visible', false);

      // Set data
      categoryAxis.data.setAll(data);

      // Create series for each value field
      valueFields.forEach((_valueField_, _index_) => {
        let series = chart.series.push(
          am5xy.SmoothedYLineSeries.new(this.root, {
            xAxis: categoryAxis,
            yAxis: valueAxis,
            valueYField: _valueField_,
            categoryXField: categoryField,
            name: _valueField_,
            visible: true,
            tension: 0.8   // 👈 Makes the line smooth
          })
        );
       
        // Stroke width
        series.strokes.template.set('strokeWidth', 3);

        // Area fill under the line (like reference chart)
        series.fills.template.setAll({
          visible: true,
          fillOpacity: 0.2
        });

        // Add bullets (with stroke like reference)
        series.bullets.push((_root_: am5.Root, _series_: any, _dataItem_: any) => {
          return am5.Bullet.new(_root_, {
            locationY: 0,
            sprite: am5.Circle.new(_root_, {
              radius: 4,
              stroke: _root_.interfaceColors.get('background'),
              strokeWidth: 2,
              fill: _series_.get('fill')
            })
          });
        });
       
        // Tooltip – series name and value on hover
        const tooltip = this._baseChartService.createStandardTooltip(this.root, 'line', {
          showValue: true,
          showCategory: true,
          showSeries: true
        });
        tooltip.label.set('text', '{name}: {valueY.formatNumber("#,##0")}\n{categoryX}');
        series.set('tooltip', tooltip);
       
        // Data
        series.data.setAll(data);
      });

      // Snap cursor to series so hover shows tooltips with series values
      cursor.set('snapToSeries', chart.series.values);

      // Add horizontal scrollbar (like reference chart)
      chart.set('scrollbarX', am5.Scrollbar.new(this.root, {
        orientation: 'horizontal'
      }));

      // Add legend like legacy implementation
      let legend = chart.children.push(
        this._baseChartService.createLegend(this.root)
      );

      // Configure legend markers like legacy implementation
      let markerTemplate = legend.markers.template;
      markerTemplate.set('width', 12);
      markerTemplate.set('height', 12);

      // Set legend data
      legend.data.setAll(chart.series.values);

      console.log('Smoothed Line Chart - Chart created successfully');

    } catch (error) {
      console.error('Error creating smoothed line chart:', error);
    }
  }

  private updateChart(data: BarChartData[]) {
    if (this.root) {
      this._baseChartService.disposeChart(this.root);
    }
    // Add a small delay to ensure proper disposal and DOM element availability
    setTimeout(() => {
      this.createChart(data);
    }, 100);
    
    // Update the chart data in the registry
    BaseChartService.updateActiveChartData(data);
  }

  ngOnDestroy(): void {
    // Hide spinner if it's still showing
    this._spinnerService.hide();
    this._baseChartService.disposeChart(this.root);
    this.smoothedLineGraphReportSubscription.unsubscribe();
  }
}
